import * as React from "react";
import { useEffect, useId, useRef } from "react";
import Spinner from "./ui/Spinner";
import { INPUT_MODES } from "../hooks/useXmlToColumns";

const MODES = [
  {
    id: INPUT_MODES.selection,
    label: "Parse selection",
    hint: "XML in Excel cells",
  },
  {
    id: INPUT_MODES.folder,
    label: "Import folder",
    hint: "Multiple .xml files",
  },
];

export default function ParseActions({
  mode,
  onModeChange,
  onParse,
  onSelectFolder,
  onParseFolder,
  onClearFolder,
  folderFileCount,
  isRunning,
  progress,
  hasAccess,
  pickError,
}) {
  const filesInputId = useId();
  const folderInputId = useId();
  const folderInputRef = useRef(null);

  useEffect(() => {
    const input = folderInputRef.current;
    if (!input) return;
    input.setAttribute("webkitdirectory", "");
    input.setAttribute("directory", "");
    input.setAttribute("mozdirectory", "");
  }, []);

  const isFolderMode = mode === INPUT_MODES.folder;
  const hasFolder = folderFileCount > 0;

  const handleFileChange = (event) => {
    const fileList = event.target.files;
    // Copy FileList before clearing the input so Excel/WebView keeps the refs.
    const files = fileList ? Array.from(fileList) : [];
    event.target.value = "";

    if (files.length === 0) {
      return;
    }

    onSelectFolder(files);
  };

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="eyebrow">Workflow</p>
          <h2 className="text-sm font-semibold tracking-tight text-slate-950">Parse XML to columns</h2>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100/80 p-0.5 ring-1 ring-slate-200/80">
        {MODES.map((item) => {
          const active = mode === item.id;
          return (
            <button
              key={item.id}
              type="button"
              disabled={isRunning}
              onClick={() => onModeChange(item.id)}
              className={[
                "rounded-lg px-2.5 py-1.5 text-left transition",
                active ? "bg-white shadow-sm ring-1 ring-slate-200/80" : "text-slate-500 hover:text-slate-700",
              ].join(" ")}
            >
              <p className={`text-xs font-semibold ${active ? "text-slate-950" : ""}`}>{item.label}</p>
              <p className="text-[10px] leading-3 text-slate-500">{item.hint}</p>
            </button>
          );
        })}
      </div>

      <p className="text-xs leading-4 text-slate-500">
        {isFolderMode
          ? "Select the Excel start cell, choose XML files, then click Parse folder."
          : "Select XML cells, then click Parse selection."}
      </p>

      <div className="subcard space-y-2 !p-2.5">
        {/*
          Excel WebView often ignores programmatic input.click() on display:none inputs.
          Labels keep the user gesture on the input itself.
        */}
        <input
          id={filesInputId}
          type="file"
          className="sr-only"
          accept=".xml,text/xml,application/xml"
          multiple
          disabled={isRunning}
          onChange={handleFileChange}
        />
        <input
          id={folderInputId}
          ref={folderInputRef}
          type="file"
          className="sr-only"
          multiple
          disabled={isRunning}
          onChange={handleFileChange}
        />

        {isFolderMode ? (
          <>
            <div className="grid grid-cols-1 gap-2">
              <label
                htmlFor={filesInputId}
                className={`btn-primary !py-2 ${isRunning ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
              >
                Choose XML files
              </label>
              <label
                htmlFor={folderInputId}
                className={`btn-secondary w-full ${isRunning ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
              >
                {hasFolder ? "Change folder" : "Choose XML folder"}
              </label>
            </div>

            {hasFolder ? (
              <div className="flex items-center justify-between gap-2">
                <p className="rounded-lg bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-100">
                  {folderFileCount} XML file(s) ready
                </p>
                <button type="button" className="btn-ghost !px-2" disabled={isRunning} onClick={onClearFolder}>
                  Clear
                </button>
              </div>
            ) : (
              <p className="text-[11px] leading-4 text-slate-500">
                Prefer <strong>Choose XML files</strong> in Excel desktop if folder pick does nothing.
              </p>
            )}

            {pickError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-700">
                {pickError}
              </div>
            )}

            <button
              type="button"
              className="btn-primary !py-2.5"
              disabled={isRunning || !hasFolder}
              onClick={onParseFolder}
            >
              {isRunning ? (
                <>
                  <Spinner />
                  Parsing folder...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                  Parse folder
                </>
              )}
            </button>
          </>
        ) : (
          <button type="button" className="btn-primary !py-2.5" disabled={isRunning} onClick={onParse}>
            {isRunning ? (
              <>
                <Spinner />
                Parsing selection...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                Parse selection
              </>
            )}
          </button>
        )}

        <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500">
          <span>{hasAccess ? "Subscription active" : "Subscription required to parse"}</span>
          {progress && (
            <span className="inline-flex items-center gap-1.5 truncate">
              {isRunning && <Spinner className="h-3 w-3 text-brand-600" />}
              {progress.message}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
